import Stack from "@mui/material/Stack";
import IconButton from "@mui/material/IconButton";
import RenderTextField from "../../../Utilities/Forms/RenderTextField";
import DataContainer from "../../../Utilities/DataContainer";
import { TextFieldElement, useFormContext } from "react-hook-form-mui";
import Grid from "@mui/material/Grid2";
import Typography from "@mui/material/Typography";
import InputAdornment from "@mui/material/InputAdornment";
import { SessionContext } from "../../../Contexts/SessionContext";
import { APIContext } from "../../../Contexts/APIContext";

import { useContext, useEffect } from "react";
import RenderSelectField from "../../../Utilities/Forms/RenderSelectField";
import yesNoDropdown from "../../../Utilities/Constants/YesNoDropdown";
import TextField from "@mui/material/TextField/TextField";
import NumberInputField from "../../../Utilities/Forms/NumberInputField";
import Tooltip from "@mui/material/Tooltip";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";

interface IProps {
    isReadOnly: boolean;
    landuse: string;
    subuse: string;
}

function gfaCondition(landuse: string, subuse: string): boolean {
    return (
        (landuse == "01" && subuse != "Q") ||
        (landuse == "02" && subuse != "H") ||
        (landuse == "04") ||
        (landuse == "05" && !"FH".includes(subuse)) ||
        (landuse == "06") ||
        (landuse == "07" && "ABCFIKOQRSTUVWX".includes(subuse)) ||
        (landuse == "14") ||
        (landuse == "15") ||
        (landuse == "16" && subuse == "B")
    );
}

function gfaConditionExtra(landuse: string, subuse: string): boolean {
    return gfaCondition(landuse, subuse) || (
        (landuse == "03" && subuse == "Q") ||
        (landuse == "13" && "ABC".includes(subuse)));
}

const hasSiteAreaExcludingPubliclyAccessibleFeatures = new Set([
    "03/A", "03/B", "03/C", "03/D", "03/F",
    "03/K", "03/L", "03/M", "03/N", "03/O", "03/P",
    "17/A", "17/B", "17/C"
]);

const siteAreaExcludingPubliclyAccessibleFeaturesDefinition = (
    <Typography>
        The amount of the total Site Area figure in hectares that excludes all publicly accessible features. All such features within a site to be excluded should consist of any of the following that are present: Publicly accessible playgrounds or recreational areas, internal public footpaths, and any internal publicly accessible woodlands, ponds, lakes or similar. Any internal parking space accessible to the public (for example marked visitor spaces or communal parking areas that are not restricted to residents through signage) are to be considered publicly accessible, whilst all residents' driveways, individually allocated bays and garages are to be considered not publicly accessible). The areas should only include the fully constructed and occupied parts of a site (in the same way as the total Site Area figure), plus any natural features that form part of the site, and should go out to the site's outermost boundaries. In the case of gated residential sites that are not accessible to the general public, the site area excluding public open space will be identical to the total site area.
    </Typography>
);

export default function Overview({ isReadOnly, landuse, subuse }: IProps) {
    const { sessionState } = useContext(SessionContext);
    const { openDialog } = useContext(APIContext);
    const { setValue, watch } = useFormContext();

    const occupiedDwellings: number | null = watch("residentialUnits.totalNumberOccupiedDwellingsAtSite");
    const siteAreaExcludingPublic: number | null = watch("siteAreaExcludingPublic");

    // For 06/BJKL the total seats is indoor + outdoor whenever either is entered (the server applies the same rule).
    const indoorSeats = watch("totalNumberOfIndoorSeats");
    const outdoorSeats = watch("totalNumberOfOutdoorSeats");
    const hasSeatSplit = (value: unknown) => value !== null && value !== undefined && value !== "";
    const seatsAreFromSplit = landuse === "06" && "BJKL".includes(subuse) && (hasSeatSplit(indoorSeats) || hasSeatSplit(outdoorSeats));

    useEffect(() => {
        if (isReadOnly || !seatsAreFromSplit) return;
        const total = (Number(indoorSeats) || 0) + (Number(outdoorSeats) || 0);
        setValue("totalNumberOfSeatsAtSite", total, { shouldDirty: true });
    }, [isReadOnly, seatsAreFromSplit, indoorSeats, outdoorSeats, setValue]);

    const openSiteAreaExcludingPublicDefinition = () => {
        openDialog(
            siteAreaExcludingPubliclyAccessibleFeaturesDefinition,
            [],
            "Site Area Excluding Publicly Accessible Features"
        );
    }

    const show = (cond: boolean) => cond;
    return (
        <DataContainer title="Overview">
            <Stack spacing={1}>
                <RenderTextField label="Trade Name" name="tradeName" readOnly={isReadOnly} alwaysShow />
                
                <NumberInputField
                    label="Site Area (hectares)"
                    name="siteArea"
                    readOnly={isReadOnly}
                    endAdornment="ha"
                />

                {show(hasSiteAreaExcludingPubliclyAccessibleFeatures.has(`${sessionState.siteListMain}/${sessionState.siteListSub}`)) && (
                    <Grid container size={12}>
                        <Grid size={8} sx={{ display: 'flex', alignItems: 'center' }}>
                            <Typography variant="body1" sx={{ lineHeight: 1.25 }}>
                                Site Area Excluding<br />
                                Publicly Accessible Features
                            </Typography>
                        </Grid>
                        <Grid size={4} sx={{ position: "relative" }}>
                            <Tooltip title="View definition">
                                <IconButton
                                    aria-label="View definition for Site Area Excluding Publicly Accessible Features"
                                    onClick={openSiteAreaExcludingPublicDefinition}
                                    size="small"
                                    sx={{
                                        p: 0,
                                        m: 0,
                                        position: "absolute",
                                        right: "calc(100% + 8px)",
                                        top: "50%",
                                        transform: "translateY(-50%)",
                                    }}
                                >
                                    <WarningAmberIcon htmlColor="#FFBF00" />
                                </IconButton>
                            </Tooltip>
                            <TextFieldElement
                                name="siteAreaExcludingPublic"
                                type="number"
                                inputMode="decimal"
                                onBlur={(event) => {
                                    if (isReadOnly) return;

                                    const v = event.target.value.trim();
                                    if (v === "" || v === "-" || v === ".") {
                                        setValue("siteAreaExcludingPublic", null, { shouldValidate: true });
                                        return;
                                    }

                                    const n = Number(v);
                                    if (!Number.isNaN(n)) {
                                        setValue("siteAreaExcludingPublic", Number(n.toFixed(2)), { shouldValidate: true });
                                    }
                                }}
                                transform={{
                                    input: (v: number | null) => {
                                        if (v == null) return "";
                                        return isReadOnly ? v.toFixed(2) : String(v);
                                    },
                                    output: (e) => {
                                        const v = e.target.value.trim();
                                        if (v === "" || v === "-" || v === ".") return null;
                                        const n = Number(v);
                                        if (Number.isNaN(n)) return null;
                                        return Number(n.toFixed(2));
                                    },
                                }}
                                inputProps={{
                                    step: 0.01,
                                }}
                                InputProps={{
                                    endAdornment: <InputAdornment position="end">ha</InputAdornment>,
                                    readOnly: isReadOnly
                                }}
                            />
                        </Grid>
                    </Grid>
                )}


                {show(gfaConditionExtra(landuse, subuse)) && (
                    <RenderTextField label="GFA (sqm)" name="gfa" readOnly={isReadOnly} endAdornment="sqm" />
                )}

                {show(gfaCondition(landuse, subuse)) && (
                    <RenderTextField label="GFA Not In Use (sqm)" name="gfaNotInUse" readOnly={isReadOnly} endAdornment="sqm" />
                )}

                {show((landuse === "01" && subuse !== "Q") || (landuse === "07" && "B".includes(subuse))) && (
                    <RenderTextField label="RFA (sqm)" name="rfa" readOnly={isReadOnly} endAdornment="sqm" />
                )}

                {show(landuse === "01" && subuse === "A") && (
                    <RenderTextField label="Non Food RFA (sqm)" name="nonFoodRFA" readOnly={isReadOnly} endAdornment="sqm" />
                )}

                {show(landuse === "01") && (
                    <RenderTextField label="Mezzanine GFA" name="mezzanineGFA" readOnly={isReadOnly} endAdornment="sqm" />
                )}
                {show(landuse === "01") && (
                    <RenderTextField label="Mezzanine RFA" name="mezzanineRFA" readOnly={isReadOnly} endAdornment="sqm" />
                )}

                <RenderTextField label="External GFA" name="externalGFA" readOnly={isReadOnly} endAdornment="sqm" />
                <RenderTextField label="Internal GFA" name="internalGFA" readOnly={isReadOnly} endAdornment="sqm" />

                {show(landuse === "01" && subuse === "Q") && (
                    <RenderTextField label="Number Of Stalls" name="numberOfStalls" readOnly={isReadOnly} />
                )}

                {show(landuse === "02" && "EF".includes(subuse)) && (
                    <RenderTextField label="External Storage" name="externalStorage" readOnly={isReadOnly} endAdornment="sqm" />
                )}

                {show(landuse === "03" && subuse === "O") && (
                    <>
                        <RenderTextField label="Number Of Beds Care" name="numberOfBedsCare" readOnly={isReadOnly} />
                        <RenderTextField label="Residents Care" name="residentsCare" readOnly={isReadOnly} />
                    </>
                )}

                {show(landuse === "06" && "AH".includes(subuse)) && (
                    <RenderTextField label="Total Number Of Bedrooms" name="totalNumberOfBedrooms" readOnly={isReadOnly} />
                )}

                {show((landuse === "03" && "OEGH".includes(subuse)) || (landuse === "05" && "FL".includes(subuse))) && (
                    <RenderTextField
                        label="Residents Total"
                        name="residentialUnits.totalNumberOfResidentsAtSite"
                        readOnly={isReadOnly}
                    />
                )}

                {show(landuse === "04" && "ABDE".includes(subuse)) && (
                    <RenderTextField label="Total Number Of Pupils" name="totalNumberOfPupils" readOnly={isReadOnly} />
                )}

                {show(landuse === "04" && subuse === "C") && (
                    <RenderTextField label="Total Number Of Students" name="totalNumberOfStudents" readOnly={isReadOnly} />
                )}

                {show((landuse === "05" && "ANBCDI".includes(subuse))) && (
                    <RenderTextField label="Total Number Of Beds" name="totalNumberOfBeds" readOnly={isReadOnly} />
                )}

                {show(landuse === "05" && "GK".includes(subuse)) && (
                    <RenderTextField label="Total Number Of Doctors" name="totalNumberOfDoctors" readOnly={isReadOnly} />
                )}

                {show(landuse === "05" && subuse === "H") && (
                    <RenderTextField label="Total Number Of Patients" name="totalNumberOfPatients" readOnly={isReadOnly} />
                )}

                {show(landuse === "05" && subuse === "M") && (
                    <RenderTextField label="Total Number Of Surgeons" name="totalNumberOfSurgeons" readOnly={isReadOnly} />
                )}

                {show(landuse === "06" && "BCDHIJKL".includes(subuse)) && (
                    <>
                        <RenderTextField label="Total Number Of Indoor Seats" name="totalNumberOfIndoorSeats" readOnly={isReadOnly} />
                        <RenderTextField label="Total Number Of Outdoor Seats" name="totalNumberOfOutdoorSeats" readOnly={isReadOnly} />
                    </>
                )}
                {show(landuse === "06" && "BJKL".includes(subuse)) && (
                    <RenderTextField label="Total Number Of Seats At Site" name="totalNumberOfSeatsAtSite" readOnly={isReadOnly || seatsAreFromSplit} />
                )}
                {show(landuse === "06" && "CDHI".includes(subuse)) && (
                    <RenderTextField
                        label="Total Number Of Seats At Site"
                        name="totalNumberOfSeatsAtSiteRfa"
                        readOnly={isReadOnly}
                    />
                )}

                {show(landuse === "06" && "DJ".includes(subuse)) && (
                    <>
                        <RenderTextField label="Pre Order Pick Up Bays" name="preOrderPickUpBays" readOnly={isReadOnly} />
                        <RenderTextField label="Drive Through Ordering Points" name="driveThroughOrderingPoints" readOnly={isReadOnly} />
                        <RenderTextField label="Drive Through Lane Capacity" name="driveThroughLaneCap" readOnly={isReadOnly} />
                        <RenderSelectField
                            label="Drive Through Lane Capacity Exceeded"
                            name="driveThroughLaneCapExceeded"
                            readOnly={isReadOnly}
                            options={yesNoDropdown}
                        />
                        <RenderSelectField
                            label="Home Delivery"
                            name="homeDeliveryServiceAvailableSite"
                            readOnly={isReadOnly}
                            options={yesNoDropdown}
                        />
                    </>
                )}

                {show((landuse === "06" && subuse === "E") || (landuse === "07" && "AHW".includes(subuse))) && (
                    <RenderTextField label="Total Number Of Seats At Site" name="totalNumberOfSeatsAtSite" readOnly={isReadOnly} />
                )}

                {show(landuse === "07" && subuse === "A") && (
                    <RenderTextField label="Total Screens" name="totalScreens" readOnly={isReadOnly} />
                )}
                {show(landuse === "07" && subuse === "B") && (
                    <RenderTextField label="Total Lanes" name="totalLanes" readOnly={isReadOnly} />
                )}

                {show(landuse === "07" && subuse === "G") && (
                    <RenderTextField label="Total Courts" name="totalCourts" readOnly={isReadOnly} />
                )}
                {show((landuse === "07" && subuse === "L") || (landuse === "11" && subuse === "A")) && (
                    <RenderTextField label="Total Pitches" name="totalPitches" readOnly={isReadOnly} />
                )}
                {show(landuse === "08" && subuse === "A") && (
                    <RenderTextField label="Total Berths" name="totalBerths" readOnly={isReadOnly} />
                )}
                {show(landuse === "09" && "ABCDEFH".includes(subuse)) && (
                    <RenderTextField label="Total Golf Holes" name="totalGolfHoles" readOnly={isReadOnly} />
                )}
                {show(landuse === "09" && subuse === "G") && (
                    <RenderTextField label="Total Ranges" name="totalRanges" readOnly={isReadOnly} />
                )}

                {show(landuse === "07" && subuse === "F") && (
                    <RenderTextField label="Ice Rink size" name="rfa" readOnly={isReadOnly} endAdornment="sqm" />
                )}

                {show((landuse === "03" && "ABCDFKLMNOP".includes(subuse)) || landuse === "17") && (
                    <>
                        {occupiedDwellings != null && siteAreaExcludingPublic != null && siteAreaExcludingPublic > 0 &&
                            <Grid container size={12}>
                                <Grid size={8} sx={{ display: 'flex', alignItems: 'center' }}>
                                    <Typography variant="body1">Housing Density</Typography>
                                </Grid>
                                <Grid size={4}>
                                    <TextField
                                        name='HousingDensity'
                                        value={(occupiedDwellings / siteAreaExcludingPublic).toFixed(2)}
                                        inputProps={{ readOnly: true }}
                                    />
                                </Grid>
                            </Grid>
                        }

                        <RenderTextField
                            label="Occupied Dwellings"
                            name="residentialUnits.totalNumberOccupiedDwellingsAtSite"
                            readOnly={isReadOnly}
                        />
                        <RenderTextField
                            label="Unoccupied Dwellings"
                            name="residentialUnits.totalNumberUnoccupiedDwellingsAtSite"
                            readOnly={isReadOnly}
                        />
                        <RenderTextField label="Total Dwellings" name="residentialUnits.totalDwellings" readOnly={isReadOnly} />
                        <RenderTextField label="Privately owned units" name="residentialUnits.privatelyOwnedUnits" readOnly={isReadOnly} />
                        <RenderTextField label="Non Privately owned units" name="residentialUnits.nonPrivatelyOwnedUnits" readOnly={isReadOnly} />
                    </>
                )}

                {show(landuse === "17" && "ABC".includes(subuse)) && (
                    <RenderTextField label="GFA Non Residential Buildings" name="gfaNonResidentialBuildings" readOnly={isReadOnly} />
                )}

                {show(
                    (landuse === "01" && !("RS".includes(subuse))) ||
                    landuse === "02" ||
                    landuse === "04" ||
                    landuse === "05" ||
                    (landuse === "06" && !("KL".includes(subuse))) ||
                    landuse === "07" ||
                    landuse === "08" ||
                    (landuse === "09" && "ABCDEFGH".includes(subuse)) ||
                    landuse === "10" ||
                    landuse === "11" ||
                    (landuse === "12" && "AC".includes(subuse)) ||
                    (landuse === "13" && "AB".includes(subuse)) ||
                    landuse === "14" ||
                    landuse === "15" ||
                    (landuse === "16" && "AB".includes(subuse))
                ) && (
                        <RenderTextField label="GFA per Employee" name="employees.gfaPerEmployee" readOnly={isReadOnly} />
                    )}

                <RenderTextField label="Year Of Site Opening" name="yearOfSiteOpening" readOnly={isReadOnly} />


                {show((landuse === "01" && subuse !== "Q") || landuse === "02" || (landuse === "03" && subuse === "J") || (landuse === "07" && "BO".includes(subuse))) && (
                    <RenderTextField label="Number Of Units" name="numberOfUnits" readOnly={isReadOnly} />
                )}

                {show(landuse === "15" && subuse === "D") && (
                    <RenderTextField label="Number Of Bays" name="bayNumber" readOnly={isReadOnly} />
                )}

                {show((landuse === "12" && subuse === "A") || (landuse === "15" && subuse === "D")) && (
                    <RenderTextField label="Total Bays" name="totalBays" readOnly={isReadOnly} />
                )}

                {show(landuse === "12" && subuse === "A") && (
                    <>
                        <RenderTextField label="Total Waste Disposal Units" name="totalWasteDisposalUnits" readOnly={isReadOnly} />
                        <RenderTextField label="Total Recycling Units" name="totalRecyclingUnits" readOnly={isReadOnly} />
                    </>
                )}

                {show(landuse === "13" && "ABC".includes(subuse)) && (
                    <>
                        <RenderTextField label="Total Charging Bays" name="totalChargingBays" readOnly={isReadOnly} />
                        <RenderTextField label="Total Charging Bays Using Units" name="totalChargingBaysUsingUnits" readOnly={isReadOnly} />
                    </>
                )}
                {show(landuse === "13" && "AB".includes(subuse)) && (
                    <>
                        <RenderTextField label="Total Filling Bays" name="totalFillingBays" readOnly={isReadOnly} />
                        <RenderTextField
                            label="Total Parking Excluding Filling Bays"
                            name="totalParkingExcludingFillingBays"
                            readOnly={isReadOnly}
                        />
                    </>
                )}
                {show(landuse === "13" && subuse === "C") && (
                    <RenderTextField
                        label="Total Parking Excluding Charging Bays"
                        name="totalParkingExcludingChargingBays"
                        readOnly={isReadOnly}
                    />
                )}
                {show(landuse === "15" && subuse === "C") && (
                    <RenderTextField
                        label="Car Hire"
                        name="carHire"
                        readOnly={isReadOnly}
                    />
                )}
            </Stack>
        </DataContainer>
    );

}
